import React, { useMemo } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import { MarketData } from '../utils/types';
import { useTranslation } from '../hooks/useTranslation';

interface BrandDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: MarketData[];
  caliber: string;
  variety?: string; // Optional: filter by variety
  exporterName: string;
  selectedCurrency: 'RMB' | 'USD';
  priceView: 'kg' | 'box';
  boxSize: number;
}

interface BrandStats {
  brand: string;
  count: number;
  medianPrice: number;
  minPrice: number;
  maxPrice: number;
  percentile: number;
}

const BrandDetailModal: React.FC<BrandDetailModalProps> = ({
  isOpen,
  onClose,
  data,
  caliber,
  variety,
  exporterName,
  selectedCurrency,
  priceView,
  boxSize,
}) => {
  const { t } = useTranslation('market');

  const brandStats = useMemo(() => {
    // Filter by caliber, variety (optional), and exporter
    const filteredData = data.filter(
      (item) =>
        item.size === caliber &&
        (variety ? item.variety === variety : true) &&
        item.exporter === exporterName
    );

    if (filteredData.length === 0) {
      return [];
    }

    // Get ALL data for this caliber (and variety if specified) for percentile calculation
    const allCaliberData = data.filter(item =>
      item.size === caliber &&
      (variety ? item.variety === variety : true)
    );
    const allCaliberPrices = allCaliberData.map(item => item.price_kg).sort((a, b) => a - b);

    // Group by brand
    const grouped = filteredData.reduce((acc, item) => {
      const brand = item.brand || t('Sin Etiqueta');

      if (!acc[brand]) {
        acc[brand] = {
          brand,
          count: 0,
          prices: [],
        };
      }

      acc[brand].count++;
      acc[brand].prices.push(item.price_kg);

      return acc;
    }, {} as Record<string, { brand: string; count: number; prices: number[] }>);

    // Calculate stats with median
    const stats: BrandStats[] = Object.values(grouped).map((group) => {
      const sortedPrices = group.prices.sort((a, b) => a - b);
      const midIndex = Math.floor(sortedPrices.length / 2);
      const medianPrice = sortedPrices.length % 2 === 0
        ? (sortedPrices[midIndex - 1] + sortedPrices[midIndex]) / 2
        : sortedPrices[midIndex];

      // Calculate percentile against ALL caliber data (not just this exporter)
      const lowerCount = allCaliberPrices.filter(p => p <= medianPrice).length;
      const percentile = (lowerCount / allCaliberPrices.length) * 100;

      return {
        brand: group.brand,
        count: group.count,
        medianPrice,
        minPrice: sortedPrices[0],
        maxPrice: sortedPrices[sortedPrices.length - 1],
        percentile,
      };
    });

    // Sort by count descending
    return stats.sort((a, b) => b.count - a.count);
  }, [data, caliber, variety, exporterName, t]);

  const totalRecords = useMemo(
    () => brandStats.reduce((sum, stat) => sum + stat.count, 0),
    [brandStats]
  );

  const overallMedianPrice = useMemo(() => {
    if (brandStats.length === 0) return 0;
    // Calculate overall median from all prices of this exporter for this caliber (and variety if specified)
    const exporterData = data.filter(
      (item) =>
        item.size === caliber &&
        (variety ? item.variety === variety : true) &&
        item.exporter === exporterName
    );
    const allPrices = exporterData.map(item => item.price_kg).sort((a, b) => a - b);
    const midIndex = Math.floor(allPrices.length / 2);
    return allPrices.length % 2 === 0
      ? (allPrices[midIndex - 1] + allPrices[midIndex]) / 2
      : allPrices[midIndex];
  }, [brandStats, data, caliber, variety, exporterName]);

  const overallPercentile = useMemo(() => {
    if (brandStats.length === 0) return 0;
    // Calculate percentile of overall median against ALL caliber (and variety if specified) data
    const allCaliberData = data.filter(item =>
      item.size === caliber &&
      (variety ? item.variety === variety : true)
    );
    const allCaliberPrices = allCaliberData.map(item => item.price_kg).sort((a, b) => a - b);
    const lowerCount = allCaliberPrices.filter(p => p <= overallMedianPrice).length;
    return (lowerCount / allCaliberPrices.length) * 100;
  }, [brandStats, data, caliber, variety, overallMedianPrice]);

  const unit = priceView === 'kg' ? '/kg' : `/${boxSize}kg box`;
  const currencySymbol = selectedCurrency === 'USD' ? '$' : '¥';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span>{t('Detalle por Etiqueta')}</span>
          </div>
          <div className="text-sm font-normal text-default-500">
            {exporterName} - {t('Calibre')} {caliber}{variety ? ` - ${t('Variedad')} ${variety}` : ''}
          </div>
        </ModalHeader>
        <ModalBody>
          {brandStats.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-default-400">
                {t('No hay datos disponibles para este calibre')}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-default-100 rounded-lg">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-default-500">{t('Total Registros')}</p>
                    <p className="text-lg font-bold">{totalRecords}</p>
                  </div>
                  <div>
                    <p className="text-sm text-default-500">{t('Precio Mediano General')}</p>
                    <p className="text-lg font-bold">
                      {currencySymbol}{overallMedianPrice.toFixed(2)} {unit}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-default-500">{t('Percentil General')}</p>
                    <p className={`text-lg font-bold ${
                      overallPercentile >= 75 ? 'text-green-600' :
                      overallPercentile >= 50 ? 'text-blue-600' :
                      overallPercentile >= 25 ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {overallPercentile.toFixed(0)}%
                    </p>
                  </div>
                </div>
              </div>

              <Table aria-label={t('Detalle por Etiqueta')}>
                <TableHeader>
                  <TableColumn>{t('Etiqueta')}</TableColumn>
                  <TableColumn align="center">{t('Registros')}</TableColumn>
                  <TableColumn align="center">{t('Mediana')}</TableColumn>
                  <TableColumn align="center">{t('Percentil')}</TableColumn>
                  <TableColumn align="center">{t('Mínimo')}</TableColumn>
                  <TableColumn align="center">{t('Máximo')}</TableColumn>
                </TableHeader>
                <TableBody>
                  {brandStats.map((stat) => (
                    <TableRow key={stat.brand}>
                      <TableCell className="font-medium">{stat.brand}</TableCell>
                      <TableCell className="text-center">{stat.count}</TableCell>
                      <TableCell className="text-center font-semibold">
                        {currencySymbol}{stat.medianPrice.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`font-semibold ${
                          stat.percentile >= 75 ? 'text-green-600' :
                          stat.percentile >= 50 ? 'text-blue-600' :
                          stat.percentile >= 25 ? 'text-orange-600' :
                          'text-red-600'
                        }`}>
                          {stat.percentile.toFixed(0)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-default-500">
                        {currencySymbol}{stat.minPrice.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center text-default-500">
                        {currencySymbol}{stat.maxPrice.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            {t('Cerrar')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BrandDetailModal;
